<template>
  <div class="select-number-list__wrap">
    <div class="select-number-list__wrap--item" v-for="(item, index) in currentSelectedOptions" :key="item.id">
      <div class="select-number-list__wrap--item__left">
        <zm-tooltip :disabled="!item.inputTooltip" placement="top">
          <template #content>
            <div v-html="item.inputTooltip"></div>
          </template>
          <zm-select data-testid="checkout-select-195" :ref='`select-${item.id}`' size="lg" @change='onSelectionChange' v-model="item.id"
                     :aria-label="getSelectLabel(item)" :disabled="item.shouldDisabled ? item.shouldDisabled : item.disabled"
                     class="select-number-list__wrap--item__select" popper-class="select-number-list-dropdown"
                     :class="{'select-number-list__wrap--item__select--simple': hasNoDesc(getOptionList(item.id))}"
                     @visible-change="(opened) => onSelectVisibleChange(opened, getOptionList(item.id))">
            <zm-select-option data-testid="checkout-option-196" v-for="option in getOptionList(item.id)" :key="option.id" :value="option.id" :label="option.desc"
                       class="select-number-list-dropdown__option">
              <zm-tooltip
                class="clipping-text"
                placement="top"
                :disabled="!selectOptionTextOverflowedMap[option.id]"
                :arrow="false"
                :content="`${option.name} ${option.desc}`"
              >
                <div :ref="`select-option-content-${option.id}`">
                  <span class="select-number-list-dropdown__title">{{ option.name }}</span>
                  <span class="select-number-list-dropdown__desc">{{ option.desc }}</span>
                </div>
              </zm-tooltip>
            </zm-select-option>
            <template #trigger="{ value, label }">
              <span class="select-number-list-dropdown__title">{{ getFilterObj(value).name }}</span>
              <span>{{ label }}</span>
            </template>
          </zm-select>
        </zm-tooltip>
        <span v-if="label" class="select-number-list__label">{{ label }}</span>
        <zm-popover v-if="getFilterObj(item.id).tooltip" read-description :placement="isLegacyModalUI ? 'left' : 'top'" trigger="hover">
          <template #content>
            <div style="min-width: 296px;" v-html="getFilterObj(item.id).tooltip"></div>
          </template>
          <tips-icon data-testid="checkout-button-197" :aria-label="getLearnMoreLabel(item)" />
        </zm-popover>
      </div>
      <div class="select-number-list__wrap--item__right">
        <!-- Expanded state: render normally -->
        <component :is="getInputNumberComponent()" v-model="item.number" v-bind="getInputNumberProps(item)" :data-testid="`checkout-input-license-${item.id}`" />

        <div class="select-number-list__wrap--item__number">
          {{ getPlanUnitByQuantity(item.number) }}
        </div>
        <zm-popover :disabled="!item.deleteTips" placement="top" trigger="hover" focus-trap>
          <template #content>
            <div v-html="item.deleteTips"></div>
          </template>
          <div>
            <zm-button
              v-if="deletable"
              data-testid="checkout-button-198"
              type="text"
              class="select-number-list__wrap--item__close"
              size="lg"
              :icon="closeIcon"
              :disabled="item.shouldDisabled ? item.shouldDisabled : item.disabled || item.disableDelete"
              :aria-label="getRemoveLabel(item)"
              @click="removeItem(item)"
            ></zm-button>
          </div>
        </zm-popover>
      </div>
      <div class="select-number-list__wrap--item__divider" v-if="index < currentSelectedOptions.length - 1"></div>
    </div>
  </div>
</template>
<script>
/**
 * Select Number List Component
 *
 * @props {{ id: string, name: string, desc: string, tooltip: string, min: number, max: number, disabled: boolean }} optionList - All select option list
 * @props {{ id: string, number: number }} modelValue - Selected option list
 * @props { boolean } deletable - Whether you can delete item
 * @props { function } beforeChange - Before change item
 * @props { string } title - Title of select number list
 * @method newItem - Add new item
 */
import { ManualCloseMark } from '@zoom/icons-vue';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import InputNumberWithPopoverUpsell from '@/components/input-number/input-number-with-popover-upsell';
import { Teleport } from 'vue';
import mxSoftBundleCustom from '../mixin/soft-bundle-pbx';
import { getPlanUnitByQuantity, planUnitMap } from '@/common/product-unit';
import { mapGetters } from 'vuex';

export default {
  name: 'SelectNumberList',
  components: {
    InputNumberWithPopover,
    InputNumberWithPopoverUpsell,
    Teleport
  },
  mixins: [mxSoftBundleCustom],
  setup() {
    return {
      closeIcon: ManualCloseMark
    };
  },
  props: {
    dataTracking: {
      type: Object,
      required: false,
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
    upsellMode: {
      type: Boolean,
      default: false,
    },
    selectedOptions: {
      type: Array,
      default: () => [],
    },
    optionList: {
      type: Array,
      default: () => [],
    },
    deletable: {
      type: Boolean,
      default: true,
    },
    beforeChange: {
      type: Function,
      default: null,
    },
    title: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    unit: {
      type: Number,
      default: planUnitMap.number,
    },
    // new add: target for teleport for select number
    numberTeleportTarget: {
      type: String,
      default: ''
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      currentSelectedOptions: [],
      testModelValue: true,
      selectOptionTextOverflowedMap: {}
    };
  },
  computed: {
    ...mapGetters(['currentQuantityMap']),
    isLegacyModalUI() {
      return this.$context?.is?.modal && !this.$context?.is?.modal2Column;
    },
    canSelectOptions() {
      return this.optionList.filter((item) => {
        return !this.currentSelectedOptions.some(option => option.id === item.id);
      });
    },
    watchCurrentSelectedOptions() {
      return JSON.parse(JSON.stringify(this.currentSelectedOptions));
    }
  },
  watch: {
    watchCurrentSelectedOptions: {
      handler(nv, ov) {
        if (this.beforeChange && !this.updating) {
          // Process data updates outside the component.
          this.beforeChange(nv, ov, (result) => {
            this.$emit('update:modelValue', this.handleWatchCurrentSelectedOptions(result, ov))
            this.$nextTick(() => {
              this.initData();
            })
          });
        } else {
          this.$emit('update:modelValue', nv.map((item) => (item)));
        }
      },
      deep: true,
    },
  },
  methods: {
    handleWatchCurrentSelectedOptions(nv, ov) {
      const defaultMapper = (item) => ({ id: item.id, number: item.number, _currentQuantity: this.currentQuantityMap.get(item.id) || 0 });
      if (!this.upsellMode) return nv.map(defaultMapper);
      const newId = nv[0]?.id;
      const oldId = ov[0]?.id;
      if (newId === oldId || !newId || !oldId) return nv.map(defaultMapper);

      const oldQty = ov[0]?.number;
      const upsellNumber = oldQty - (this.currentQuantityMap.get(oldId) || 0);
      const newCurrentQuantity = this.currentQuantityMap.get(newId) || 0;
      const nextQty = upsellNumber + newCurrentQuantity;
      return [{ id: newId, number: nextQty, _currentQuantity: newCurrentQuantity }]
    },
    initData() {
      this.updating = true;
      this.currentSelectedOptions = this.modelValue.map((item) => {
        const option = this.optionList.find(option => option.id === item.id);
        return {
          ...option,
          ...item,
        };
      });
      this.$nextTick(() => {
        this.updating = false;
      });
    },
    getOptionList(activeId) {
      const currentActiveOption = this.currentSelectedOptions.find(o => o.id === activeId);
      const activeItem = this.optionList.find(option => option.id === activeId);

      if (this.soft_bundle_hiddenTypeSwitch) { // block customer to switch uc | mc types
        return [activeItem];
      }

      const otherSelectOptions = currentActiveOption?.disableSwitch? [] : this.canSelectOptions;
      return [activeItem, ...otherSelectOptions].filter(option => Boolean(option))
    },
    hasNoDesc(options) {
      return options.every(x => !x.desc);
    },
    getFilterObj(keyValue) {
      if (this.optionList.length === 0) {
        return {id: '', name: '', desc: '', tooltip: ''}
      }
      const foundOption = this.optionList.find(option => option.id === keyValue);
      if (!foundOption) {
        return {id: '', name: '', desc: '', tooltip: ''}
      }
      return foundOption;
    },
    getSelectLabel(item) {
      return `${this.title} ${item.name}`;
    },
    getRemoveLabel(item) {
      const {name, desc} = this.getFilterObj(item.id);
      return `Remove ${this.title} ${name} ${desc}`
    },
    getLearnMoreLabel(item) {
      const { name } = this.getFilterObj(item.id);
      return name
    },
    removeItem(item) {
      this.currentSelectedOptions = this.currentSelectedOptions.filter(option => option.id !== item.id);
      if (this.dataTracking) {
        this.$tracking.buyInteraction(this.dataTracking?.buyInteraction || '',
            `${this.dataTracking?.buySelection || ''} ${item?.name || ''} ${item?.desc || ''} - remove`);
      }
    },
    newItem() {
      let defaultNumber = 1;
      if (this.upsellMode) {
        defaultNumber = this.canSelectOptions[0]._currentQuantity + 1;
      }
      this.canSelectOptions.length && this.currentSelectedOptions.push({
        ...this.canSelectOptions[0],
        number: defaultNumber,
      });
    },
    onSelectionChange(id) {
      if (this.dataTracking) {
        let name = this.optionList.find(item => item.id === id)?.name || '';
        let desc = this.optionList.find(item => item.id === id)?.desc || '';
        this.$tracking.buyInteraction(this.dataTracking?.buyInteraction || '',
            `${this.dataTracking?.buySelection || ''} ${name} ${desc} - select`);
      }
      /** *
       * ZOOM-679898
       * Since the zbo-select component changes with the change of the element ID,
       * the previous element cannot be focused,
       * so it is necessary to manually focus on the new zbo-select component.
       */
      this.$nextTick(() => {
        const targetRef = this.$refs[`select-${id}`];
        const refEle = targetRef?.[0]?.$el;
        if (refEle) {
          const innerEle = refEle.querySelector('.zbo-select-span__inner');
          innerEle && innerEle.focus();
        }
      });
    },
    getPlanUnitByQuantity(quantity) {
      return getPlanUnitByQuantity(this.unit, quantity);
    },
    // Get InputNumber component based on upsell mode
    getInputNumberComponent() {
      return this.upsellMode ? InputNumberWithPopoverUpsell : InputNumberWithPopover;
    },
    // Get InputNumber component props with unified logic
    getInputNumberProps(item) {
      const commonProps = {
        'data-tracking': this.dataTracking,
        size: 'lg',
        min: item.min,
        max: item.max,
        'controls-position': 'both',
        label: `${item.name} ${item.desc} Select the number of plans`,
        disabled: item.disabled,
        decreaseTips: item.decreaseTips,
        teleportTarget: this.numberTeleportTarget,
      };

      // If in upsell mode, add oldNumber prop
      if (this.upsellMode) {
        return {
          ...commonProps,
          oldNumber: item._currentQuantity || 0,
        };
      }

      return commonProps;
    },
    /**
     * Check if the select option text is overflowing and set the overflowed state to the map
     * @param opened
     * @param options options list
     */
    onSelectVisibleChange(opened, options) {
      if (!opened) {
        return;
      }
      setTimeout(() => {
        options.forEach(option => {
          const contentRef = this.$refs[`select-option-content-${option.id}`]?.[0];
          if (!contentRef) {
            return;
          }
          this.selectOptionTextOverflowedMap[option.id] = contentRef.scrollWidth > contentRef.clientWidth;
        });
      });
    },
  },
  created() {
    this.initData()
  },
}
</script>
<style lang="scss">
@import '@/styles/scss-utils.scss';

.select-number-list {
  &__wrap {
    margin: 0;

    &--item {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;

      &__divider {
        width: 100%;
        height: 1px;
        background-color: var(--zoom-color-border-subtle-neutral, #DFE3E8);
        margin: 12px 0;
      }

      &__right {
        display: flex;
        align-items: center;
      }

      &__select {
        width: 265px;

        &--simple {
          width: 200px;
        }

        .zbo-select-input.is-disabled {
          .select-number-list-dropdown__title {
            color: var(--zoom-color-text-neutral, #686F79);
          }
        }
      }

      &__number {
        font-weight: 500;
        color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
        margin-left: 8px;
        display: inline-block;
        min-width: 64px;
        white-space: nowrap;
        text-align: left;
      }

      &__close {
        color: var(--zoom-color-text-neutral, #686F79) !important;

        &:not(.is-text-disabled):hover {
          color: var(--zoom-color-fill-error, #DA1639) !important;
          background: none !important;
        }
      }
    }
  }

  &__label {
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    padding-left: 8px;
    line-height: 19px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
  }
}

.select-number-list-dropdown {
  &__title {
    font-size: 18px;
    font-weight: 700;
    color: var(--zoom-color-text-primary, #0d6bde);
    white-space: nowrap;
    margin-right: 4px;
  }

  &__desc {
    font-size: 14px;
    font-weight: 500;
    color: var(--zoom-color-text-neutral, #686F79);
  }
}
</style>

<style lang="scss" scoped>
@import '@zoom/billing-responsive/styles/responsive.scss';

.clipping-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.select-number-list {
  &__wrap--item {
    &__left {
      display: flex;
      align-items: center;
      gap: 8px;
      @include media(mobile,tablet){
        flex-direction: row;
        width: 100%;
      }
    }
  }
}
</style>
